"use client";

import React from "react";
import { observer } from "mobx-react-lite";
import { useStore } from "../services/store";
import { formatPattern, extractPatternChars } from "./util-filter";
import { colorMapping } from "./util-color";
import { MouseOverPopover } from "./Popover";
import Candle from "./Candle";
import SymbolIframe from "./SymbolIframe";
import { addWatch } from "./util-api";
import { getSectorBySymbol, findMatchingSectorSymbols, SectorMap } from "./symbols";
import { sortSectorSymbolsByPercentChange, getBestSectorForSymbol } from "./util-symbol-grouping";
import { Toast } from "./Toast";
import StrongSymbolRow from "./StrongSymbolRow";
import SymbolThumbnail from "./SymbolThumbnail";

const ERSymbolRow = observer(function ERSymbolRow({
  symInfo,
  isOpen,
  onToggle,
  sector,
  colCh = 10,
  erData = [], // ER data for finding related symbols
}) {
  const store = useStore();
  const [toastMsg, setToastMsg] = React.useState(null);
  const [toastType, setToastType] = React.useState("success");
  const [showRelatedDropdown, setShowRelatedDropdown] = React.useState(false);

  let sym;
  let erVal;
  let er;
  let symSector = sector;
  if (typeof symInfo === "object" && symInfo !== null) {
    sym = symInfo.symbol;
    erVal = symInfo.erVal;
    er = symInfo.er;
    if (symInfo.sector) symSector = symInfo.sector;
  } else {
    sym = symInfo;
  }

  // Find the actual sector this symbol belongs to (like StrongSymbolRow)
  const actualSector = React.useMemo(() => {
    if (!sym) return null;
    const s = sym.toLowerCase();
    const matchingSectors = [];
    
    // Find all sectors that contain this symbol
    for (const [sectorKey, sectorSymbols] of Object.entries(SectorMap)) {
      if (Array.isArray(sectorSymbols) && sectorSymbols.includes(s)) {
        matchingSectors.push(sectorKey);
      }
    }
    
    if (matchingSectors.length === 0) return null;
    if (matchingSectors.length === 1) return matchingSectors[0];
    
    // Choose the sector with the longest length (more fine-grained)
    return matchingSectors.reduce((longest, current) => 
      current.length > longest.length ? current : longest
    );
  }, [sym]);

  // Get sector symbols for dropdown (like StrongSymbolRow)
  const sectorSymbols = React.useMemo(() => {
    if (!actualSector) return [];
    const symbols = findMatchingSectorSymbols(actualSector);
    const symbolsWithData = symbols.map((sym) => {
      const pattern = store.patternMap?.[sym] || "";
      const symbolSector = getBestSectorForSymbol(sym, actualSector);
      return [sym, { pattern, sector: symbolSector }];
    });
    return sortSectorSymbolsByPercentChange(
      symbolsWithData,
      store.currentGridMap,
      store.formattedDate
    );
  }, [actualSector, store.patternMap, store.currentGridMap, store.formattedDate]);

  if (!sym) return null;

  // Use primary sector for color mapping instead of earnings sector
  const primarySector = getSectorBySymbol(sym) || symSector;
  const chipBg = colorMapping(primarySector);
  const s = (sym || "").toLowerCase();
  const signalPattern =
    (typeof symInfo === "object" && symInfo?.pattern) || store.patternMap?.[s] || "";
  const pattern = formatPattern(signalPattern);
  const inPosSignals = store.signalPosSymbols?.has(s);
  const inNegSignals = store.signalNegSymbols?.has(s);
  const inSignals = inPosSignals || inNegSignals;

  const closeToast = () => setToastMsg(null);

  const handleBuy = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const sec = symSector || getSectorBySymbol(sym);
    const curTick = store.gridMap?.[s]?.[0] ?? store.gridMap?.[sym]?.[0];
    const tick = curTick
      ? {
          ...curTick,
          symbol: s,
          lo: curTick.low,
          close: curTick.close,
          sector: sec,
        }
      : { symbol: s, sector: sec };

    addWatch(
      {
        ticks: [
          {
            ...tick,
            pattern: signalPattern,
            orderType: 1,
            signalStrength: 5,
          },
        ],
        date: store.formattedDate,
      },
      () => {
        store.addSignalSymbol({
          symbol: s,
          orderType: 1,
          sector: sec,
          signalStrength: 5,
        });

        setToastType("success");
        setToastMsg(`Buy ${s.toUpperCase()} added`);
      },
    );
  };

  const handleSell = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const sec = symSector || getSectorBySymbol(sym);
    const curTick = store.gridMap?.[s]?.[0] ?? store.gridMap?.[sym]?.[0];
    const tick = curTick
      ? {
          ...curTick,
          symbol: s,
          lo: curTick.low,
          close: curTick.close,
          sector: sec,
        }
      : { symbol: s, sector: sec };

    addWatch(
      {
        ticks: [
          {
            ...tick,
            pattern: signalPattern,
            orderType: -1,
            signalStrength: 5,
          },
        ],
        date: store.formattedDate,
      },
      () => {
        store.addSignalSymbol({
          symbol: s,
          orderType: -1,
          sector: sec,
          signalStrength: 5,
        });

        setToastType("success");
        setToastMsg(`Sell ${s.toUpperCase()} added`);
      },
    );
  };

  // get pct (like StrongSymbolRow)
  const ticks = store.currentGridMap?.[s] || store.currentGridMap?.[sym] || [];
  const [today, prev] = ticks;

  const hasData = !!(
    today &&
    prev &&
    Number.isFinite(+today.close) &&
    Number.isFinite(+prev.close)
  );
  const close = hasData ? +today.close : null;
  const prevClose = hasData ? +prev.close : null;
  const delta = hasData ? close - prevClose : 0;
  const pct = hasData && prevClose !== 0 ? (delta / prevClose) * 100 : null;

  return (
    <div style={{ marginBottom: 6 }}>
      {/* symbol row */}
      <div
        role="button"
        tabIndex={0}
        onClick={() => {
          // Don't open iframe if related dropdown is showing
          if (!showRelatedDropdown) {
            onToggle(sym);
            // Set the sector when clicking on the symbol (like StrongSymbolRow)
            const sectorToSet = actualSector || symSector;
            if (sectorToSet) {
              store.setSector(sectorToSet);
            }
          }
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            if (!showRelatedDropdown) {
              onToggle(sym);
              // Set the sector when clicking on the symbol (like StrongSymbolRow)
              const sectorToSet = actualSector || symSector;
              if (sectorToSet) {
                store.setSector(sectorToSet);
              }
            }
          }
        }}
        title={sym}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 6,
          fontSize: 12,
          cursor: "pointer",
          userSelect: "none",
          color: "black",
        }}
      >
        {/* Left side: symbol */}
        <span
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <span
            style={{
              width: `${colCh}ch`,
              display: "inline-block",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              backgroundColor: chipBg !== "none" ? chipBg : "white",
              borderRadius: 999,
              border: "1px solid #e5e7eb",
              textAlign: "center",
              flex: "0 0 auto",
              paddingTop: 2,
              paddingBottom: 2,
              color: "black",
              fontSize: 14,
            }}
          >
            <MouseOverPopover
              symbol={sym}
              sector={symSector}
              tick={{ symbol: sym, sector: symSector }}
              doHover={false}
            >
              {sym.toUpperCase()}
            </MouseOverPopover>
          </span>
          {inSignals && (
            <span
              title={inPosSignals ? "Signal +" : "Signal -"}
              style={{
                width: 12,
                height: 12,
                borderRadius: "50%",
                backgroundColor: inPosSignals
                  ? "rgba(34,197,94,.9)"
                  : "rgba(239,68,68,.9)",
                boxShadow: "0 0 0 2px rgba(0,0,0,.4)",
                flex: "0 0 auto",
              }}
            />
          )}
        </span>

        {/* Right side: marketCap + pattern/percentage + erVal + chevron */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            flex: 1,
            justifyContent: "flex-end",
          }}
        >
          {store.marketCapMap?.[sym] && (
            <span
              style={{
                color: "#9ca3af",
                fontSize: 11,
                minWidth: 30,
                textAlign: "right",
              }}
            >
              {Math.round(store.marketCapMap[sym])}b
            </span>
          )}

          <span
            onClick={(e) => {
              console.log('ERSymbolRow click debug:', {
                allowDrillDown: store.allowDrillDown,
                sectorSymbolsLength: sectorSymbols.length,
                sectorSymbols: sectorSymbols,
                actualSector: actualSector,
                symSector: symSector,
                sym: sym
              });
              
              if (store.allowDrillDown && sectorSymbols.length > 1) {
                e.preventDefault();
                e.stopPropagation();
                setShowRelatedDropdown(!showRelatedDropdown);
                console.log('Toggling dropdown to:', !showRelatedDropdown);
                // Ensure iframe doesn't open when related dropdown is toggled
                if (!showRelatedDropdown) {
                  // Don't call onToggle since we're showing dropdown instead
                }
              }
            }}
            style={{
              minWidth: 60,
              textAlign: "right",
              color: "#d1d5db",
              fontSize: 11,
              cursor: (store.allowDrillDown && sectorSymbols.length > 1) ? "pointer" : "default",
              backgroundColor: store.allowDrillDown ? "rgba(255,0,0,0.2)" : "transparent", // Debug: red background when drill down enabled
              padding: "6px 8px", // Larger padding for easier clicking
              borderRadius: 4, // Add border radius for better visual feedback
            }}
            title={(store.allowDrillDown && sectorSymbols.length > 1) ? `Click to show ${actualSector} sector symbols` : ""}
          >
            {/* Show pattern when enabled in percentage mode */}
            {store.isPercent && store.showPattern && pattern && (
              <span
                style={{
                  marginRight: "6px",
                  fontSize: "11px",
                  color: "#999",
                  cursor: "pointer",
                }}
                title={pattern}
              >
                {extractPatternChars(pattern)}
              </span>
            )}

            {!store.isPercent
              ? pattern
              : hasData
                ? `${delta >= 0 ? "+" : ""}${pct.toFixed(1)}%`
                : ""}
            
            {/* Show V/VV pattern when isPercent is true and pattern contains V */}
            {store.isPercent && !store.showPattern && pattern && (pattern.includes('V') || pattern.includes('v')) && (
              <span style={{ marginLeft: "4px", fontSize: "11px", color: "#999" }}>
                {pattern.match(/V+|v+/g)?.[0] || ""}
              </span>
            )}
          </span>

          <SymbolThumbnail symbol={sym} />

          {erVal !== undefined && (
            <span style={{ color: "#9ca3af", fontSize: 11 }}>{er}</span>
          )}

          {store.isAdmin && store.isAdminParam && store.allowSignal && (
            <>
              <span
                onClick={handleBuy}
                title="Buy"
                style={{
                  fontSize: 12,
                  lineHeight: 1,
                  opacity: 0.55,
                  cursor: "pointer",
                  userSelect: "none",
                  color: "rgba(34,197,94,.95)",
                  paddingRight: "6px",
                }}
              >
                B
              </span>
              <span
                onClick={handleSell}
                title="Sell"
                style={{
                  fontSize: 12,
                  lineHeight: 1,
                  opacity: 0.55,
                  cursor: "pointer",
                  userSelect: "none",
                  color: "rgba(239,68,68,.95)",
                  paddingRight: "6px",
                }}
              >
                S
              </span>
            </>
          )}

          <Candle symbol={sym} />

          <svg
            aria-hidden
            width="10"
            height="10"
            viewBox="0 0 24 24"
            style={{
              transform: isOpen ? "rotate(90deg)" : "rotate(0deg)",
              transition: "transform 120ms ease",
              opacity: 0.6,
              flex: "0 0 auto",
            }}
          >
            <path
              d="M9 6l6 6-6 6"
              fill="none"
              stroke="#9ca3af"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </div>

      {/* iframe for symbol */}
      {isOpen && !showRelatedDropdown && <SymbolIframe symbol={sym} />}

      {/* sector symbols dropdown */}
      {showRelatedDropdown && (
        <div style={{ marginTop: 8 }}>
          <div
            className="font-mono text-sm whitespace-pre leading-5 near-white bg-near-black"
            style={{
              marginBottom: 8,
              padding: 8,
              paddingLeft: 12,
              borderRadius: 8,
              backgroundColor: "rgba(20,83,45,.3)",
              boxShadow: "0 2px 6px rgba(0,0,0,.25)",
              maxHeight: "300px",
              overflow: "auto",
            }}
          >
            {sectorSymbols.map(([symbolKey, valObj]) => (
              <StrongSymbolRow
                key={symbolKey}
                sym={symbolKey}
                val={valObj}
                isPos={true}
                colCh={10}
                hideDropdown={true}
              />
            ))}
          </div>
        </div>
      )}

      {toastMsg && (
        <Toast
          message={toastMsg}
          type={toastType}
          onClose={closeToast}
          duration={1500}
        />
      )}
    </div>
  );
});

export default ERSymbolRow;
